import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { uploadFileViaSignedUrl } from "@/lib/upload";
import type { PortalJourneyTaskAttachment } from "./types";

// Three-step direct-to-Supabase upload (journey-task-specific endpoints).
// Uses the shared `uploadFileViaSignedUrl` helper relocated to `@/lib/upload`
// in sub-bucket 15. Endpoints under /portal/journeys/attachments/* take
// progress_id in the request body (not the URL) so a single set of
// endpoints serves any progress row.

export function useUploadTaskAttachment(progressId: string) {
  const queryClient = useQueryClient();
  return useMutation<PortalJourneyTaskAttachment, ApiClientError, File>({
    mutationFn: (file) =>
      uploadFileViaSignedUrl<PortalJourneyTaskAttachment>(file, {
        initEndpoint: "/portal/journeys/attachments/upload-init",
        finalizeEndpoint: "/portal/journeys/attachments",
        buildInitBody: ({ file }) => ({
          progress_id: progressId,
          file_name: file.name,
          content_type: file.type || "application/octet-stream",
          size_bytes: file.size,
        }),
        buildFinalizeBody: ({ storageKey, file }) => ({
          progress_id: progressId,
          storage_key: storageKey,
          file_name: file.name,
          content_type: file.type || "application/octet-stream",
          size_bytes: file.size,
        }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.portal.journeys.taskAttachments(progressId),
      });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to upload attachment");
    },
  });
}
